/*
 * Buno Plus Intercept Screen Manager
 * Handles the display and interaction logic for the Buno Plus upsell screen
 */

window.BunoPlusIntercept = (function() {
  'use strict';
  
  let currentScreen = null;
  let isProcessing = false;
  
  /**
   * Show the intercept screen for a specific section
   * @param {string} sectionId - The section ID
   * @param {string} context - The context ('cart-drawer' or 'cart-page')
   */
  function show(sectionId, context) {
    console.log('🎯 Showing Buno Plus intercept screen for section:', sectionId);
    
    const screen = document.getElementById('buno-plus-intercept-' + sectionId);
    if (!screen) {
      console.error('🎯 Intercept screen not found for section:', sectionId);
      return;
    }
    
    currentScreen = screen;
    
    // Show the screen
    screen.style.display = 'flex';
    
    // Trigger animation after a brief delay
    requestAnimationFrame(() => {
      screen.classList.add('visible');
    });
    
    // Set up event listeners
    setupEventListeners(screen, sectionId, context);
    
    // Focus management for accessibility
    const closeButton = screen.querySelector('.buno-plus-intercept-close');
    if (closeButton) {
      closeButton.focus();
    }
    
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    
    // Track event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'buno_plus_intercept_shown', {
        section_id: sectionId,
        context: context
      });
    }
  }
  
  /**
   * Hide the currently displayed intercept screen
   */
  function hide() {
    if (!currentScreen) return;
    
    console.log('🎯 Hiding Buno Plus intercept screen');
    
    // Start hide animation
    currentScreen.classList.remove('visible');
    
    // Actually hide after animation completes
    setTimeout(() => {
      if (currentScreen) {
        currentScreen.style.display = 'none';
        currentScreen = null;
      }
    }, 300);
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Clean up event listeners
    cleanupEventListeners();
  }
  
  /**
   * Set up event listeners for the intercept screen
   * @param {HTMLElement} screen - The screen element
   * @param {string} sectionId - The section ID
   * @param {string} context - The context
   */
  function setupEventListeners(screen, sectionId, context) {
    // Close button and backdrop clicks
    screen.addEventListener('click', handleScreenClick);
    
    // Keyboard events
    document.addEventListener('keydown', handleKeyDown);
    
    // Button clicks
    const buttons = screen.querySelectorAll('[data-action]');
    buttons.forEach(button => {
      button.addEventListener('click', (event) => handleButtonClick(event, sectionId, context));
    });
  }
  
  /**
   * Clean up event listeners
   */
  function cleanupEventListeners() {
    if (currentScreen) {
      currentScreen.removeEventListener('click', handleScreenClick);
    }
    document.removeEventListener('keydown', handleKeyDown);
  }
  
  /**
   * Handle clicks on the screen (backdrop and close button)
   * @param {Event} event - The click event
   */
  function handleScreenClick(event) {
    // Close if clicking backdrop or close button
    if (event.target === currentScreen || 
        event.target.closest('.buno-plus-intercept-close')) {
      event.preventDefault();
      hide();
    }
  }
  
  /**
   * Handle keyboard events
   * @param {KeyboardEvent} event - The keyboard event
   */
  function handleKeyDown(event) {
    if (!currentScreen) return;
    
    // Close on Escape key
    if (event.key === 'Escape') {
      event.preventDefault();
      hide();
    }
    
    // Trap focus within the modal
    if (event.key === 'Tab') {
      trapFocus(event);
    }
  }
  
  /**
   * Trap focus within the modal for accessibility
   * @param {KeyboardEvent} event - The tab event
   */
  function trapFocus(event) {
    const focusableElements = currentScreen.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }
  
  /**
   * Handle button clicks within the intercept screen
   * @param {Event} event - The click event
   * @param {string} sectionId - The section ID
   * @param {string} context - The context
   */
  function handleButtonClick(event, sectionId, context) {
    if (isProcessing) return;
    
    const button = event.target.closest('[data-action]');
    if (!button) return;
    
    const action = button.dataset.action;
    console.log('🎯 Button clicked:', action);
    
    switch (action) {
      case 'close':
        hide();
        break;
        
      case 'add-and-checkout':
        handleAddAndCheckout(button, sectionId, context);
        break;
        
      case 'continue-without':
        handleContinueWithout(button, sectionId, context);
        break;
    }
  }
  
  /**
   * Handle "Add Buno Plus & Checkout" button click
   * @param {HTMLElement} button - The button element
   * @param {string} sectionId - The section ID
   * @param {string} context - The context
   */
  function handleAddAndCheckout(button, sectionId, context) {
    if (isProcessing) return;
    
    console.log('🎯 Adding Buno Plus and proceeding to checkout');
    isProcessing = true;
    
    // Show loading state
    button.classList.add('buno-plus-intercept-button-loading');
    button.disabled = true;
    
    // Track event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'buno_plus_intercept_add_clicked', {
        section_id: sectionId,
        context: context
      });
    }
    
    // Check if BunoPlusManager is available for this section
    if (!window.BunoPlusManager || !window.BunoPlusManager[sectionId]) {
      console.error('🎯 BunoPlusManager not available for section:', sectionId);
      showError(button, 'Unable to add Buno Plus. Please try again.');
      return;
    }
    
    // Add Buno Plus to cart
    window.BunoPlusManager[sectionId].addToCart()
      .then(() => {
        console.log('🎯 Buno Plus added successfully, proceeding to checkout');
        
        // Track success
        if (typeof gtag !== 'undefined') {
          gtag('event', 'buno_plus_intercept_add_success', {
            section_id: sectionId,
            context: context
          });
        }
        
        // Hide the intercept screen
        hide();
        
        // Proceed to checkout after a brief delay to allow cart updates
        setTimeout(() => {
          proceedToCheckout();
        }, 500);
      })
      .catch((error) => {
        console.error('🎯 Failed to add Buno Plus:', error);
        showError(button, 'Failed to add Buno Plus. Please try again.');
        
        // Track error
        if (typeof gtag !== 'undefined') {
          gtag('event', 'buno_plus_intercept_add_error', {
            section_id: sectionId,
            context: context,
            error: error.message || 'Unknown error'
          });
        }
      });
  }
  
  /**
   * Handle "Continue Without Buno Plus" button click
   * @param {HTMLElement} button - The button element
   * @param {string} sectionId - The section ID
   * @param {string} context - The context
   */
  function handleContinueWithout(button, sectionId, context) {
    console.log('🎯 Continuing without Buno Plus');
    
    // Track event
    if (typeof gtag !== 'undefined') {
      gtag('event', 'buno_plus_intercept_continue_clicked', {
        section_id: sectionId,
        context: context
      });
    }
    
    // Hide the intercept screen
    hide();
    
    // Proceed to checkout
    setTimeout(() => {
      proceedToCheckout();
    }, 100);
  }
  
  /**
   * Show error message on button
   * @param {HTMLElement} button - The button element
   * @param {string} message - The error message
   */
  function showError(button, message) {
    isProcessing = false;
    button.classList.remove('buno-plus-intercept-button-loading');
    button.disabled = false;
    
    // Show error state
    const originalText = button.textContent;
    button.textContent = message;
    button.style.background = '#dc2626';
    
    // Reset after 3 seconds
    setTimeout(() => {
      button.textContent = originalText;
      button.style.background = '';
    }, 3000);
  }
  
  /**
   * Proceed to checkout by submitting the cart form
   */
  function proceedToCheckout() {
    console.log('🎯 Final step: Proceeding to checkout');
    
    // Find the cart form and submit it
    const cartForm = document.getElementById('cart');
    if (cartForm) {
      // Create a hidden input for the checkout action if it doesn't exist
      let checkoutInput = cartForm.querySelector('input[name="checkout"]');
      if (!checkoutInput) {
        checkoutInput = document.createElement('input');
        checkoutInput.type = 'hidden';
        checkoutInput.name = 'checkout';
        checkoutInput.value = '';
        cartForm.appendChild(checkoutInput);
      }
      
      // Submit the form
      cartForm.submit();
    } else {
      console.warn('🎯 Cart form not found, using fallback redirect');
      // Fallback: redirect to checkout
      window.location.href = '/checkout';
    }
  }
  
  // Public API
  return {
    show: show,
    hide: hide
  };
})();

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  console.log('🎯 Buno Plus Intercept Manager initialized');
});